import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { ArrowLeft, Mail, Phone as PhoneIcon, MapPin, Calendar, Briefcase, Sparkles, Pencil, Check, X, Loader2 } from 'lucide-react'
import { formatPhoneDisplay } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { format } from 'date-fns'

interface Profile {
  id: string
  name: string
  email: string
  role: string
  status: string
  avatarUrl?: string
  lastActive?: string
  createdAt: string
  bio?: string
  position?: string
  department?: string
  phone?: string
  location?: string
  yearsAtFirm?: string
  funFacts?: string
}

export default function TeamProfilePage() {
  const { userId } = useParams()
  const navigate = useNavigate()
  const { agent } = useAuth()
  const { toast } = useToast()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editData, setEditData] = useState<Partial<Profile>>({})

  const isOwnProfile = userId === agent?.id
  const canEdit = isOwnProfile || agent?.role === 'admin'

  useEffect(() => {
    loadProfile()
  }, [userId])

  const loadProfile = async () => {
    try {
      const res = await api.get(`/api/auth/profile/${userId}`)
      if (res.data.success) {
        setProfile(res.data.profile)
        setEditData(res.data.profile)
      }
    } catch {
      toast({ title: 'Profile not found', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.put(`/api/auth/profile/${userId}`, {
        bio: editData.bio,
        position: editData.position,
        department: editData.department,
        phone: editData.phone,
        location: editData.location,
        yearsAtFirm: editData.yearsAtFirm,
        funFacts: editData.funFacts,
      })
      setProfile(prev => prev ? { ...prev, ...editData } : null)
      setEditing(false)
      toast({ title: 'Profile updated' })
    } catch {
      toast({ title: 'Failed to update profile', variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6 px-3 sm:px-0 overflow-hidden">
        <div className="h-8 w-32 bg-muted animate-pulse rounded" />
        <div className="h-48 bg-muted animate-pulse rounded-xl" />
        <div className="h-32 bg-muted animate-pulse rounded-xl" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <p className="text-muted-foreground">Profile not found</p>
        <Button variant="ghost" onClick={() => navigate(-1)} className="mt-4">Go back</Button>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6 px-3 sm:px-0 overflow-hidden">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-muted-foreground">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      {/* Profile Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5">
            <Avatar className="h-16 w-16 sm:h-20 sm:w-20">
              <AvatarImage src={profile.avatarUrl || undefined} />
              <AvatarFallback className="text-2xl bg-gradient-to-br from-primary/80 to-primary text-white font-bold">
                {profile.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 text-center sm:text-left">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-3">
                <h1 className="text-xl sm:text-2xl font-heading font-bold">{profile.name}</h1>
                <Badge variant={profile.role === 'admin' ? 'default' : 'secondary'}>
                  {profile.role}
                </Badge>
                {(profile as any).isSuperAdmin && (
                  <Badge className="border-transparent bg-[#ede9fe] dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 shadow-sm">
                    Super Admin
                  </Badge>
                )}
              </div>
              {profile.position && (
                <p className="text-muted-foreground mt-0.5">{profile.position}</p>
              )}
              <p className="text-sm text-muted-foreground mt-1">{profile.email}</p>
              <div className="flex items-center justify-center sm:justify-start gap-3 mt-3">
                <Button size="sm" variant="outline" onClick={() => navigate(`/messages?contact=${profile.id}`)}>
                  <Mail className="h-3.5 w-3.5 mr-1.5" />
                  Message
                </Button>
                {canEdit && !editing && (
                  <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    Edit Profile
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* About */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <h3 className="font-heading font-semibold">About</h3>

          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Bio</label>
                <Textarea
                  value={editData.bio || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell the team about yourself..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Position / Title</label>
                  <Input
                    value={editData.position || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, position: e.target.value }))}
                    placeholder="e.g. Senior Associate"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Department</label>
                  <Input
                    value={editData.department || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, department: e.target.value }))}
                    placeholder="e.g. Litigation"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Phone</label>
                  <Input
                    value={editData.phone || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Location</label>
                  <Input
                    value={editData.location || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g. Los Angeles, CA"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Years at Firm</label>
                  <Input
                    value={editData.yearsAtFirm || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, yearsAtFirm: e.target.value }))}
                    placeholder="e.g. 3 years"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Fun Facts</label>
                <Textarea
                  value={editData.funFacts || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, funFacts: e.target.value }))}
                  placeholder="Hobbies, interests, fun facts the team should know..."
                  rows={2}
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditData(profile) }}>
                  <X className="h-3.5 w-3.5 mr-1.5" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {profile.bio ? (
                <p className="text-sm text-foreground/80 leading-relaxed">{profile.bio}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  {isOwnProfile ? 'Add a bio so your team knows who you are' : 'No bio yet'}
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {profile.position && (
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span>{profile.position}</span>
                  </div>
                )}
                {profile.department && (
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span>{profile.department}</span>
                  </div>
                )}
                {profile.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <PhoneIcon className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${profile.phone.replace(/\D/g, '')}`} className="text-primary hover:underline">
                      {formatPhoneDisplay(profile.phone)}
                    </a>
                  </div>
                )}
                {profile.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{profile.location}</span>
                  </div>
                )}
                {profile.yearsAtFirm && (
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span>{profile.yearsAtFirm} at the firm</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Joined {format(new Date(profile.createdAt), 'MMMM yyyy')}</span>
                </div>
              </div>

              {profile.funFacts && (
                <div className="pt-2 border-t border-border/50">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Fun Facts</span>
                  </div>
                  <p className="text-sm text-foreground/80">{profile.funFacts}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
